import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useDispatch, useSelector } from 'react-redux';
import {
    AddCircleBlue,
    MinLoader,
    NoItemsYet,
    TextRow,
    StatusWidthColorsSelector,
    MoreActions,
    SimpleTooltip,
    ManageActions,
    AddButton,
    ToolInfo, EditWithIcon,
} from 'components';
import {
    Colors,
    FindLoad,
    FindSuccessItem,
    goalDataTypeEnums,
    Images,
    PermissionList,
    RolePermission,
    useBaseModal,
    useModal,
    renderTargetStatuses,
    targetStatusEnums,
    isMaintenanceRunning,
} from 'utils';
import { goalActions, httpRequestsOnSuccessActions } from 'store';
import { GoalTargetStatus, isTargetStatusLocked, targetOptionsTooltip } from './goalTargetStatus';
import { MaintenanceInfo } from './maintenanceInfo';
import { BaselineDetails } from '../baseline';
import { TargetsChooseFromLibrary } from '../../../../../clinicalFragments';
import { Svg } from 'assets';

const CHANGE_MAINTENANCE_STEP_ACTION = 'CHANGE_MAINTENANCE_STEP';
export const GoalTargets = ({ requestParams }) => {
    const goalById = useSelector((state) => state.goals.goalById);
    const client = useSelector((state) => state.client.clientItemInfo);
    const dispatch = useDispatch();
    const [targetRow, setTargetRow] = useState(null);
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const [targets, setTargets] = useState([]);
    const TARGET_ACTION_TYPE = inputs?.id ? 'EDIT_TARGET' : 'CREATE_TARGET';
    const loadAdd = FindLoad(TARGET_ACTION_TYPE);
    const successCreate = FindSuccessItem(TARGET_ACTION_TYPE);
    const { open } = useModal();
    const { openBase } = useBaseModal();
    const checkNoOptionCase =
        goalById?.dataType === goalDataTypeEnums?.DURATION ||
        goalById?.dataType === goalDataTypeEnums?.FREQUENCY ||
        goalById?.dataType === goalDataTypeEnums?.SCORE ||
        goalById?.dataType === goalDataTypeEnums?.RATE ||
        goalById?.dataType === goalDataTypeEnums?.IRT;
    const [loadId, setLoadId] = useState('');
    const loadStepChange = FindLoad(CHANGE_MAINTENANCE_STEP_ACTION);
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    // Edit and create rows share `inputs`; only one is open at a time.
    const statusTooltip = targetOptionsTooltip(goalById, inputs?.status);

    useEffect(() => {
        if (goalById?.targets?.length) {
            setTargets(goalById.targets);
        }
    }, [goalById]);

    useEffect(() => {
        if (successCreate) {
            resetCreation();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(TARGET_ACTION_TYPE));
        }
    }, [successCreate]);

    const resetCreation = () => {
        setTargetRow(null);
        setError('');
        setInputs({});
    };

    const handleSave = () => {
        if (inputs?.name && inputs?.status) {
            const params = {
                ...inputs,
                goal: goalById?.id,
            };
            if (inputs?.id) {
                delete params?.id;
                delete params?.updatedAt;
                delete params?.createdAt;
                dispatch(goalActions.editTarget(goalById?.id, inputs?.id, params, requestParams));
            } else {
                dispatch(goalActions.createTarget(goalById?.id, params, requestParams));
            }
        } else {
            setError(!inputs?.name ? 'name' : !inputs?.status ? 'status' : '');
        }
    };

    const handleChange = (e) => {
        setInputs((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
        if (error === e.target.name) {
            setError('');
        }
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination || source.index === destination.index) return;
        const updatedTargets = Array.from(targets);
        const [movedItem] = updatedTargets.splice(source.index, 1);
        updatedTargets.splice(destination.index, 0, movedItem);
        setTargets(updatedTargets);

        const updatedGoal = {
            ...goalById,
            targets: updatedTargets,
        };

        dispatch(
            goalActions.reorderTarget(updatedGoal, {
                indexFrom: source.index,
                indexTo: destination.index,
            })
        );
    };

    const handleAction = (ev, item, name) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (name === 'edit') {
            setTargetRow(item?.id);
            setInputs(item);
        }
        // if (name === 'chart') {
        //     open(<TargetProgressModal goal={goalById} clientId={client?.id} target={item} />);
        // }
    };

    const addTarget = (closePopover) => {
        closePopover?.();

        setTargetRow('new');
        // const status = (checkNoOptionCase || goalById?.options?.length) ? targetStatusEnums.TEACHING : targetStatusEnums.PENDING;
        setInputs({ status: targetStatusEnums.PENDING });
    };

    const openTargetDetails = (target) => {
        openBase(<BaselineDetails target={target} />);
    };

    const handleChangeStep = (item, step) => {
        setLoadId(item?.id);
        const params = {
            targetId: item?.id,
            step: step + 1,
        };
        dispatch(goalActions.changeMaintenanceStep(goalById?.id, params));
    };

    return (
        <div>
            {targets?.length > 0 ? (
                <div className="goal-target-wrapper">
                    <div className="goal-target-header">
                        <div style={{ width: '5%' }}>#</div>
                        <div style={{ width: '20%' }}>Target Name</div>
                        <div style={{ width: '47%' }}>Objective</div>
                        <div style={{ width: '19%' }}>Status</div>
                        <div style={{ width: '9%' }}>Action</div>
                    </div>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="targetList">
                            {(provided) => (
                                <div className="goal-target-body-wrapper" {...provided.droppableProps} ref={provided.innerRef}>
                                    {targets.map((i, index) => (
                                        <Draggable key={i.id} draggableId={i.id.toString()} index={index} isDragDisabled={!hasPermission}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                    }}>
                                                    {targetRow === i?.id ? (
                                                        <div className="create-target-wrapper">
                                                            <input
                                                                value={inputs?.name || ''}
                                                                onChange={handleChange}
                                                                placeholder={'Task Name'}
                                                                name="name"
                                                                type="text"
                                                                style={{
                                                                    width: '20%',
                                                                    borderColor: error === 'name' ? '#FE7070' : '#E4E7EC',
                                                                }}
                                                            />
                                                            <input
                                                                value={inputs?.description || ''}
                                                                onChange={handleChange}
                                                                placeholder={'Add Objective '}
                                                                name="description"
                                                                type="text"
                                                                style={{
                                                                    width: '60%',
                                                                    borderColor: error === 'description' ? '#FE7070' : '#E4E7EC',
                                                                }}
                                                            />
                                                            <div style={{ width: '12%' }}>
                                                                {/* The selector renders a fragment, so the tooltip needs an element to hold its ref. */}
                                                                <SimpleTooltip title={statusTooltip} placement="bottom" arrow>
                                                                    <div>
                                                                        <StatusWidthColorsSelector
                                                                            disabled={checkNoOptionCase ? false : !goalById?.options?.length}
                                                                            styles={{
                                                                                border: `1px solid ${error === 'status' ? '#FE7070' : '#E4E7EC'}`,
                                                                            }}
                                                                            dropdownOptions={renderTargetStatuses}
                                                                            selected={inputs?.status}
                                                                            onPass={(e) =>
                                                                                handleChange({
                                                                                    target: {
                                                                                        name: 'status',
                                                                                        value: e?.status,
                                                                                    },
                                                                                })
                                                                            }
                                                                        />
                                                                    </div>
                                                                </SimpleTooltip>
                                                            </div>
                                                            <div className="flex-align-center" style={{ gap: 4, width: '10%' }}>
                                                                <button onClick={handleSave} className="add-target-btn">
                                                                    {loadAdd?.length ? (
                                                                        <MinLoader
                                                                            margin="3px 0 0"
                                                                            color={Colors.TextWhite}
                                                                            position="relative"
                                                                        />
                                                                    ) : (
                                                                        'Save'
                                                                    )}
                                                                </button>
                                                                <button onClick={resetCreation} className="cancel-add-target-btn">
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => openTargetDetails(i)}
                                                            className="goal-target-body"
                                                            style={{
                                                                border: `1px solid ${snapshot.isDragging ? '#0C66E3' : '#F9FAFB'}`,
                                                                background: '#F9FAFB',
                                                            }}>
                                                            <div className="goal-target-body-row" style={{ width: '5%' }}>
                                                                {index + 1}
                                                            </div>
                                                            <div
                                                                className="goal-target-body-row flex-align-center"
                                                                style={{ width: '20%', gap: '4px' }}>
                                                                {!i?.mastery?.followGoalSettings && (
                                                                    <SimpleTooltip
                                                                        title={<p>{'This target has its own mastery.'}</p>}
                                                                        placement="bottom"
                                                                        arrow>
                                                                        <img src={Images.masteredTarget} alt="mastered" />
                                                                    </SimpleTooltip>
                                                                )}
                                                                <TextRow name={i?.name} />
                                                            </div>
                                                            <div className="goal-target-body-row" style={{ width: '47%' }}>
                                                                <p style={{height: 30, overflow: 'auto'}}>{i?.description} </p>
                                                                {/*<TextRow name={i?.description} />*/}
                                                            </div>
                                                            <div onClick={(e) => e.stopPropagation()}
                                                                className="goal-target-body-row"
                                                                style={{ width: '19%' }}
                                                            >

                                                                <GoalTargetStatus
                                                                    target={i}
                                                                    goalById={goalById}
                                                                    requestParams={requestParams}
                                                                    noAction={isTargetStatusLocked(goalById)}
                                                                />
                                                                {/* Step controls for a mastered target whose maintenance is running. */}
                                                                {isMaintenanceRunning(i) ? (
                                                                    (i?.maintenance?.followGoalSettings ? goalById?.maintenance?.steps?.length > 0 : i?.maintenance?.steps?.length > 0) ? (
                                                                        <ManageActions
                                                                            disabled={
                                                                                i?.maintenance?.followGoalSettings
                                                                                    ? goalById?.maintenance?.autoMaintenance
                                                                                        ? !goalById?.maintenance?.steps?.length
                                                                                        : true
                                                                                    : i?.maintenance?.autoMaintenance
                                                                                    ? !i?.maintenance?.steps?.length
                                                                                    : true
                                                                            }
                                                                            headerTitle={'Maintenance Step'}
                                                                            btnClass="reset-btn"
                                                                            renderBtnView={() => {
                                                                                const showIcon = i?.maintenance?.followGoalSettings
                                                                                    ? goalById?.maintenance?.autoMaintenance
                                                                                        ? !!goalById?.maintenance?.steps?.length
                                                                                        : false
                                                                                    : i?.maintenance?.autoMaintenance
                                                                                    ? !!i?.maintenance?.steps?.length
                                                                                    : false;

                                                                                return (
                                                                                    <div className="target-step-box">
                                                                                        <p>
                                                                                            {i?.maintenance?.followGoalSettings
                                                                                                ? `${
                                                                                                      i?.maintenanceState
                                                                                                          ?.maintenanceStep || 0
                                                                                                  }/${
                                                                                                      goalById?.maintenance?.steps
                                                                                                          ?.length || 0
                                                                                                  }`
                                                                                                : `${
                                                                                                      i?.maintenanceState
                                                                                                          ?.maintenanceStep || 0
                                                                                                  }/${i?.maintenance?.steps?.length || 0}`}
                                                                                        </p>

                                                                                        {!!loadStepChange?.length && loadId === i?.id ? (
                                                                                            <MinLoader
                                                                                                height={12}
                                                                                                width={12}
                                                                                                color={'##475467'}
                                                                                                margin={'0'}
                                                                                                position={'relative'}
                                                                                            />
                                                                                        ) : (
                                                                                            showIcon && (
                                                                                                <img src={Images.dropDown} alt="down" />
                                                                                            )
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            }}
                                                                            renderBody={(closePopover) => {
                                                                                const steps = i?.maintenance?.followGoalSettings
                                                                                    ? goalById?.maintenance?.steps
                                                                                    : i?.maintenance?.steps;
                                                                                return (
                                                                                    <div className="target-maintenance-step-box">
                                                                                        {steps?.map((item, j) => (
                                                                                            <div
                                                                                                onClick={() => {
                                                                                                    handleChangeStep(i, j);
                                                                                                    closePopover?.();
                                                                                                }}>
                                                                                                <button>
                                                                                                    {`Step ${j + 1}/${steps?.length}`}
                                                                                                </button>
                                                                                                {i?.maintenanceState?.maintenanceStep ===
                                                                                                    j + 1 && (
                                                                                                    <img
                                                                                                        src={Images.checkCircleBlue}
                                                                                                        alt=""
                                                                                                    />
                                                                                                )}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                );
                                                                            }}
                                                                            anchorOrigin={{
                                                                                vertical: 'bottom',
                                                                                horizontal: 'right',
                                                                            }}
                                                                            transformOrigin={{
                                                                                vertical: 'top',
                                                                                horizontal: 'right',
                                                                            }}
                                                                            styleBodyItems={{ padding: 0 }}
                                                                        />
                                                                    ) : (
                                                                        <>
                                                                            <ToolInfo text={'No maintenance steps are configured.'} />
                                                                        </>
                                                                    )
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                <MaintenanceInfo
                                                                    target={i}
                                                                    goal={goalById}
                                                                    config={i?.maintenance?.followGoalSettings ? goalById?.maintenance : i?.maintenance}
                                                                />
                                                            </div>
                                                            <div className="goal-target-body-row flex-align-center" style={{ width: '9%', gap: 16 }}>
                                                                {hasPermission &&
                                                                <EditWithIcon
                                                                    icon={Images.edit}
                                                                    type="button"
                                                                    handleClick={(e) => handleAction(e, i, 'edit')}
                                                                />
                                                                }

                                                                {/*<MoreActions*/}
                                                                {/*    item={i}*/}
                                                                {/*    buttonsList={*/}
                                                                {/*        hasPermission*/}
                                                                {/*            ? [*/}
                                                                {/*                  {*/}
                                                                {/*                      text: 'Edit',*/}
                                                                {/*                      icon: Images.edit,*/}
                                                                {/*                      actionName: 'edit',*/}
                                                                {/*                  },*/}
                                                                {/*                  // {*/}
                                                                {/*                  //     text: 'Chart',*/}
                                                                {/*                  //     icon: Images.lineChart,*/}
                                                                {/*                  //     actionName: 'chart',*/}
                                                                {/*                  // },*/}
                                                                {/*                  {*/}
                                                                {/*                      text: 'Events',*/}
                                                                {/*                      icon: Images.eventCalendar,*/}
                                                                {/*                      actionName: 'event',*/}
                                                                {/*                  },*/}
                                                                {/*              ]*/}
                                                                {/*            : [*/}
                                                                {/*                  // {*/}
                                                                {/*                  //     text: 'Chart',*/}
                                                                {/*                  //     icon: Images.lineChart,*/}
                                                                {/*                  //     actionName: 'chart',*/}
                                                                {/*                  // },*/}
                                                                {/*                  {*/}
                                                                {/*                      text: 'Events',*/}
                                                                {/*                      icon: Images.eventCalendar,*/}
                                                                {/*                      actionName: 'event',*/}
                                                                {/*                  },*/}
                                                                {/*              ]*/}
                                                                {/*    }*/}
                                                                {/*    handleAction={handleAction}*/}
                                                                {/*/>*/}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            ) : (
                <NoItemsYet
                    height={'250px'}
                    text={'You don’t have Targets yet'}
                    subTitle={'Nothing new for now. Enjoy the silence while it lasts'}
                    image={Images.noClientYet}
                    containerStyles={{ position: 'relative' }}
                    renderButton={() =>
                        hasPermission ? (
                            <ManageActions
                                btnClass="reset-btn"
                                renderBtnView={() => (
                                    <div style={{ width: 'fit-content', marginTop: 24 }}>
                                        <AddButton styles={{ width: 'fit-content' }} text={'Add Target'} />
                                    </div>
                                )}
                                renderBody={(closePopover) => (
                                    <div className="flex" style={{ flexDirection: 'column' }}>
                                        <button className="header-add-goals-items-btn" onClick={() => addTarget(closePopover)}>
                                            Add new target
                                        </button>

                                        <button
                                            className="header-add-goals-items-btn"
                                            onClick={() => {
                                                open(
                                                    <TargetsChooseFromLibrary
                                                        CHOOSE_ACTION_TYPE="CHOOSE_TARGETS_FROM_LIBRARY"
                                                        action={goalActions.chooseTargetsFromLibrary}
                                                        goalById={goalById}
                                                    />,
                                                    {
                                                        styles: {
                                                            wrapperStyle: {
                                                                width: 'calc(100vw - 32px)',
                                                                maxWidth: '1370px',
                                                            },
                                                        },
                                                    }
                                                );
                                                closePopover?.();
                                            }}>
                                            Choose from Library
                                        </button>
                                    </div>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                styleBodyItems={{ padding: 0 }}
                            />
                        ) : null
                    }
                />
            )}

            {targetRow === 'new' && (
                <div className="create-target-wrapper">
                    <input
                        value={inputs?.name || ''}
                        onChange={handleChange}
                        placeholder={'Task Name'}
                        name="name"
                        type="text"
                        style={{ width: '20%', borderColor: error === 'name' ? '#FE7070' : '#E4E7EC' }}
                    />
                    <input
                        value={inputs?.description || ''}
                        onChange={handleChange}
                        placeholder={'Add Objective '}
                        name="description"
                        type="text"
                        style={{ width: '60%', borderColor: error === 'description' ? '#FE7070' : '#E4E7EC' }}
                    />
                    <div style={{ width: '12%' }}>
                        <SimpleTooltip title={statusTooltip} placement="bottom" arrow>
                            <div>
                                <StatusWidthColorsSelector
                                    disabled={checkNoOptionCase ? false : !goalById?.options?.length}
                                    styles={{ border: `1px solid ${error === 'status' ? '#FE7070' : '#E4E7EC'}` }}
                                    dropdownOptions={renderTargetStatuses}
                                    selected={inputs?.status}
                                    onPass={(e) =>
                                        handleChange({
                                            target: {
                                                name: 'status',
                                                value: e?.status,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </SimpleTooltip>
                    </div>
                    <div className="flex-align-center" style={{ gap: 4, width: '10%' }}>
                        <button onClick={handleSave} className="add-target-btn">
                            {loadAdd?.length ? <MinLoader margin="3px 0 0" color={Colors.TextWhite} position="relative" /> : 'Add'}
                        </button>
                        <button onClick={resetCreation} className="cancel-add-target-btn">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {!targetRow && !!targets?.length && hasPermission && (
                <div className="flex-end" style={{ margin: '8px 0 0' }}>
                    <ManageActions
                        btnClass="reset-btn"
                        renderBtnView={(isOpen) => (
                            <div className="flex-align-center" style={{ gap: 8 }}>
                                <AddCircleBlue text={'Add Target'} />

                                <Svg.ArrowRight2Svg
                                    style={{
                                        transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                        filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                    }}
                                />
                            </div>
                        )}
                        renderBody={(closePopover) => (
                            <div className="flex" style={{ flexDirection: 'column' }}>
                                <button className="header-add-goals-items-btn" onClick={() => addTarget(closePopover)}>
                                    Add new target
                                </button>

                                <button
                                    className="header-add-goals-items-btn"
                                    onClick={() => {
                                        open(
                                            <TargetsChooseFromLibrary
                                                CHOOSE_ACTION_TYPE="CHOOSE_TARGETS_FROM_LIBRARY"
                                                action={goalActions.chooseTargetsFromLibrary}
                                                goalById={goalById}
                                            />,
                                            {
                                                styles: {
                                                    wrapperStyle: {
                                                        width: 'calc(100vw - 32px)',
                                                        maxWidth: '1370px',
                                                    },
                                                },
                                            }
                                        );
                                        closePopover?.();
                                    }}>
                                    Choose from Library
                                </button>
                            </div>
                        )}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        styleBodyItems={{ padding: 0 }}
                    />
                </div>
            )}
        </div>
    );
};
